import React, { useMemo, useCallback } from "react";
import { OverlayView, OverlayViewF } from "@react-google-maps/api";
import { MapLocation } from "../../models/models";
import { dt } from "../../../../shared/themes/redesignTokens";

// ===================================================================
//  DESIGN TOKENS
// ===================================================================


// Priority colors (consistent with CitySidebar chips)
const PRIORITY_COLORS = {
  urgent: "#E74C3C",
  high: "#E67E22",
  low: "#27AE60",
};

// ===================================================================
//  MARKER SIZE SCALE
// ===================================================================

const getMarkerSize = (count: number): number => {
  if (count <= 1) return 36;
  if (count <= 3) return 42;
  if (count <= 9) return 48;
  return 56;
};

// ===================================================================
//  PRIORITY HELPERS
// ===================================================================

/**
 * Determine the highest priority color from counts.
 * Priority order: urgent > high > low.
 */
const getHighestPriorityColor = (
  urgentCount: number,
  highCount: number,
  _lowCount: number
): string => {
  if (urgentCount > 0) return PRIORITY_COLORS.urgent;
  if (highCount > 0) return PRIORITY_COLORS.high;
  return PRIORITY_COLORS.low;
};

/**
 * Build a conic-gradient string from priority counts.
 * Segments ordered: urgent (red) -> high (orange) -> low (green).
 */
const buildConicGradient = (
  urgentCount: number,
  highCount: number,
  lowCount: number,
  total: number
): string => {
  if (total === 0) return PRIORITY_COLORS.low;

  const segments: { color: string; count: number }[] = [];
  if (urgentCount > 0) segments.push({ color: PRIORITY_COLORS.urgent, count: urgentCount });
  if (highCount > 0) segments.push({ color: PRIORITY_COLORS.high, count: highCount });
  if (lowCount > 0) segments.push({ color: PRIORITY_COLORS.low, count: lowCount });

  // Single color: return solid
  if (segments.length <= 1) {
    return segments[0]?.color ?? PRIORITY_COLORS.low;
  }

  // Build conic-gradient stops
  let currentDeg = 0;
  const stops: string[] = [];
  for (const seg of segments) {
    const deg = (seg.count / total) * 360;
    stops.push(`${seg.color} ${currentDeg}deg ${currentDeg + deg}deg`);
    currentDeg += deg;
  }

  return `conic-gradient(${stops.join(", ")})`;
};

// ===================================================================
//  COMPONENT
// ===================================================================

interface CityMarkerProps {
  location: MapLocation;
  isSelected: boolean;
  onClick: (location: MapLocation) => void;
}

const CityMarker: React.FC<CityMarkerProps> = ({ location, isSelected, onClick }) => {
  const hasUrgentOrHigh = location.urgentCount > 0 || location.highCount > 0;
  const size = getMarkerSize(location.totalCount);

  // Check if there's only a single company across all requests
  const uniqueCompanyIds = useMemo(() => {
    const ids = new Set<string>();
    for (const req of location.requests) {
      ids.add(req.customer.id);
    }
    return ids;
  }, [location.requests]);

  const isSingleCompany = uniqueCompanyIds.size === 1;
  const singleCompanyRequest = isSingleCompany ? location.requests[0] : null;
  const singleLogoUrl = singleCompanyRequest?.customer?.logoUrl;
  const singleFirstLetter = singleCompanyRequest?.customer?.name?.charAt(0)?.toUpperCase() || "?";

  const labelText = isSingleCompany && singleCompanyRequest?.customer?.name
    ? singleCompanyRequest.customer.name
    : location.city;

  const position = useMemo(
    () => ({ lat: location.latitude, lng: location.longitude }),
    [location.latitude, location.longitude]
  );

  const handleClick = useCallback(() => {
    onClick(location);
  }, [onClick, location]);

  const getPixelPositionOffset = useCallback(
    () => ({ x: -(size / 2), y: -(size / 2 + 10) }),
    [size]
  );

  const highestPriorityColor = useMemo(
    () => getHighestPriorityColor(
      location.urgentCount,
      location.highCount,
      location.lowCount
    ),
    [location.urgentCount, location.highCount, location.lowCount]
  );

  // Background style: conic gradient with priority colors
  const backgroundStyle = useMemo(() => {
    return buildConicGradient(
      location.urgentCount,
      location.highCount,
      location.lowCount,
      location.totalCount
    );
  }, [location.urgentCount, location.highCount, location.lowCount, location.totalCount]);

  // Pulse ring color matches the highest priority
  const pulseColor = highestPriorityColor;

  return (
    <OverlayViewF
      position={position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={getPixelPositionOffset}
    >
      <div
        onClick={handleClick}
        style={{
          position: "relative",
          width: size,
          height: size,
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Pulsing ring for urgent/high markers */}
        {hasUrgentOrHigh && (
          <div
            style={{
              position: "absolute",
              width: size,
              height: size,
              borderRadius: "50%",
              border: `2px solid ${pulseColor}`,
              animation: "markerPulse 2.5s ease-in-out infinite",
              top: 0,
              left: 0,
            }}
          />
        )}

        {/* Main circle */}
        <div
          style={{
            width: size,
            height: size,
            borderRadius: "50%",
            background: backgroundStyle,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: isSelected
              ? `0 0 0 3px #FFFFFF, 0 0 0 5px ${highestPriorityColor}`
              : "0 2px 8px rgba(0,0,0,0.25)",
            transition: "box-shadow 0.2s ease, transform 0.2s ease",
            transform: isSelected ? "scale(1.1)" : "scale(1)",
          }}
        >
          {isSingleCompany ? (
            // Single company: show company logo or first-letter avatar
            <div
              style={{
                width: size - 6,
                height: size - 6,
                borderRadius: "50%",
                backgroundColor: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {singleLogoUrl ? (
                <img
                  src={singleLogoUrl}
                  alt=""
                  style={{
                    width: size - 8,
                    height: size - 8,
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <span
                  style={{
                    fontFamily: dt.font,
                    fontSize: size <= 36 ? "14px" : "16px",
                    fontWeight: 700,
                    color: dt.textSecondary,
                    userSelect: "none",
                  }}
                >
                  {singleFirstLetter}
                </span>
              )}
            </div>
          ) : (
            // Multiple companies: show count number
            <span
              style={{
                color: "#FFFFFF",
                fontSize: size <= 36 ? "13px" : size <= 42 ? "14px" : "16px",
                fontWeight: 700,
                fontFamily: dt.font,
                lineHeight: 1,
                userSelect: "none",
              }}
            >
              {location.totalCount}
            </span>
          )}

          {/* Request count badge (always shown, 2x size) */}
          {isSingleCompany && (
            <div
              style={{
                position: "absolute",
                top: -14,
                right: -14,
                minWidth: 36,
                height: 36,
                borderRadius: "18px",
                backgroundColor: highestPriorityColor,
                color: "#FFFFFF",
                fontSize: "20px",
                fontWeight: 700,
                fontFamily: dt.font,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0 6px",
                border: "2px solid #FFFFFF",
                lineHeight: 1,
                boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
              }}
            >
              {location.totalCount}
            </div>
          )}
        </div>

        {/* Label below */}
        <div
          style={{
            position: "absolute",
            bottom: -18,
            left: "50%",
            transform: "translateX(-50%)",
            fontFamily: dt.font,
            fontSize: "13px",
            fontWeight: 700,
            color: dt.textPrimary,
            whiteSpace: "nowrap",
            textShadow: "0 1px 3px rgba(255,255,255,0.9)",
            userSelect: "none",
          }}
        >
          {labelText}
        </div>
      </div>
    </OverlayViewF>
  );
};

export default React.memo(CityMarker);
